<script setup>
  import {computed, inject, onMounted, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentSignatureHistoryDialog from "@components/Dialogs/SignatureHistoryDialog.vue";
  import ComponentSignPatientDocumentsDialog from "@components/Dialogs/SignPatientDocumentsDialog.vue";
  import PatientsPatientDocumentRow from "@pages/Patients/Components/PatientDocumentRow.vue";
  import {confirmationLabels} from "@pages/Patients/Enums/Enums.js";
  import {MedButton, MedLink, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientDocuments",
  });

  const props = defineProps({
    patient: {
      type: Object,
      default: () => ({}),
    },
  });

  const privacy = inject("$privacy");

  const refSignPatientDocumentsDialog = ref(null);
  const refSignatureHistoryDialog = ref(null);

  const internalPatient = ref(null);

  onMounted(async () => {
    internalPatient.value = await privacy.decryptPatient(props.patient);
  });

  const customizedPdfUrl = computed(() => {
    const fullName = (internalPatient.value?.firstname + " " + internalPatient.value?.lastname).trim();
    const base64Name = btoa(
      new TextEncoder().encode(fullName).reduce((data, byte) => data + String.fromCodePoint(byte), ""),
    ).replace(/=+$/, ""); // remove "=" padding from b64 string

    return (
      route("sign-patient-documents.download.privacy-consent-pdf", {patientId: props.patient.id}) +
      `?name=${encodeURIComponent(base64Name)}`
    );
  });
</script>

<template>
  <ComponentCard
    id="patient-documents"
    title="Dokumente"
    class="rounded-lg border-2 border-gray-200"
    :class="$attrs.class"
    :test-id="getTestId('patient-documents')"
  >
    <h6>
      BAK Unterlagen (handschriftliche Signatur erforderlich)
      <MedLink
        variant="primary"
        href="/url-redirect/pdl-agreement-short"
        target="_blank"
        :data-test="getTestId('patient-documents-pdl-agreement-link')"
      >
        (Word-Datei)
      </MedLink>
    </h6>
    <ul class="mt-2 space-y-2">
      <li>
        <PatientsPatientDocumentRow attribute-name="agreementpdlconfirmed" :patient="patient" :test-id="testId">
          {{ confirmationLabels["agreementpdlconfirmed"] }} *
        </PatientsPatientDocumentRow>
      </li>

      <li>
        <PatientsPatientDocumentRow
          attribute-name="releasefromconfidentialityconfirmed"
          :patient="patient"
          :test-id="testId"
        >
          {{ confirmationLabels["releasefromconfidentialityconfirmed"] }}
        </PatientsPatientDocumentRow>
      </li>
    </ul>

    <h6 class="mt-4 border-t-2 pt-4">
      Einwilligung zur Datenverarbeitung
      <MedLink
        variant="primary"
        :href="customizedPdfUrl"
        target="_blank"
        :data-test="getTestId('patient-documents-customized-pdf-link')"
      >
        <span class="whitespace-nowrap">(personalisiertes PDF)</span>
      </MedLink>
    </h6>

    <ComponentAlert v-if="!!$page.props.client.privacy_responsible_person === false" class="mt-2 text-sm" type="info">
      Hinterlegen Sie die
      <MedLink
        variant="primary"
        :href="$route('settings.show', {user: privacy.getCurrentUser().id})"
        :data-test="getTestId('patient-documents-privacy-settings-link')"
      >
        Kontaktdaten des Datenschutzbeauftragten
      </MedLink>
      für ein vollständiges PDF-Dokument.
    </ComponentAlert>

    <ul class="mt-2 space-y-2">
      <li>
        <patients-patient-document-row attribute-name="datasecurityconfirmed" :patient="patient">
          {{ confirmationLabels["datasecurityconfirmed"] }} *
        </patients-patient-document-row>
      </li>

      <li>
        <patients-patient-document-row attribute-name="datasecurityresearchconfirmed" :patient="patient">
          {{ confirmationLabels["datasecurityresearchconfirmed"] }}
        </patients-patient-document-row>
      </li>

      <li>
        <patients-patient-document-row attribute-name="datasecurityaiconfirmed" :patient="patient">
          {{ confirmationLabels["datasecurityaiconfirmed"] }}
        </patients-patient-document-row>
      </li>
    </ul>

    <div class="mt-2 text-right text-sm">* Einwilligung verpflichtend</div>

    <template #actions>
      <div class="flex w-full flex-row-reverse items-center justify-between space-x-reverse">
        <MedButton
          variant="primary"
          :data-test="getTestId('patient-documents-sign-button')"
          @click="refSignPatientDocumentsDialog.open()"
        >
          Digital signieren
        </MedButton>

        <MedTooltip content="Verlauf der Einwilligungen">
          <MedButton
            aria-label="Verlauf der Einwilligungen anzeigen"
            icon="folder_copy"
            rounded="full"
            :data-test="getTestId('patient-documents-signature-history-button')"
            @click="refSignatureHistoryDialog.open()"
          />
        </MedTooltip>
      </div>
    </template>
  </ComponentCard>

  <ComponentSignPatientDocumentsDialog ref="refSignPatientDocumentsDialog" :patient="patient" />
  <ComponentSignatureHistoryDialog ref="refSignatureHistoryDialog" :patient="patient" />
</template>
